import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box, Typography, TextField, IconButton, Rating, Card, CardContent,
    Chip, InputAdornment, Select, MenuItem, Menu, Alert, Skeleton, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from "@mui/material";
import SearchIcon              from "@mui/icons-material/Search";
import ClearIcon               from "@mui/icons-material/Clear";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import KeyboardArrowLeft        from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight       from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon            from "@mui/icons-material/OpenInNew";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import PersonRoundedIcon         from "@mui/icons-material/PersonRounded";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS = {
    "On-Trip":     { bg: T.indigoLt,  color: T.indigo,  dot: T.indigo  },
    "Active":      { bg: T.emeraldLt, color: T.emerald, dot: T.emerald },
    "Inactive":    { bg: T.amberLt,   color: T.amber,   dot: T.amber   },
    "Maintenance": { bg: T.roseLt,    color: T.rose,    dot: T.rose    },
};

const StatusChip = ({ status }) => {
    const cfg = STATUS[status] || STATUS["Inactive"];
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.4, borderRadius: "8px", bgcolor: cfg.bg, border: `1px solid ${cfg.color}22` }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{status}</Typography>
        </Box>
    );
};

// ── Filter pill ───────────────────────────────────────────────────────────
const FilterPill = ({ label, active, color, bg, onClick }) => (
    <Box onClick={onClick} sx={{
        px: 1.6, py: 0.6, borderRadius: "8px", cursor: "pointer",
        bgcolor: active ? bg : "transparent",
        color:   active ? color : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 12.5,
        border: `1.5px solid ${active ? color + "44" : T.border}`,
        transition: "all 0.14s", whiteSpace: "nowrap",
        "&:hover": { bgcolor: bg, color },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>{label}</Box>
);

// ── Capacity formatter ────────────────────────────────────────────────────
const fmtCapacity = (kg, space) => {
    const kgStr    = kg    != null ? `${Number(kg).toLocaleString("en-IN")} kg` : null;
    const spaceStr = space != null ? `${Number(space).toFixed(2)} m³` : null;
    if (kgStr && spaceStr) return `${kgStr} · ${spaceStr}`;
    return kgStr || spaceStr || "—";
};

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkeletonRow = () => (
    <TableRow>
        {[160, 100, 130, 90, 120, 50].map((w, i) => (
            <TableCell key={i}><Skeleton width={w} height={20} /></TableCell>
        ))}
    </TableRow>
);

// ── Main component ────────────────────────────────────────────────────────
const STATUS_FILTERS = [
    { label: "All",         value: "all",         color: T.teal,    bg: T.tealLt    },
    { label: "Active",      value: "active",       color: T.emerald, bg: T.emeraldLt },
    { label: "On-Trip",     value: "on-trip",      color: T.indigo,  bg: T.indigoLt  },
    { label: "Inactive",    value: "inactive",     color: T.amber,   bg: T.amberLt   },
    { label: "Maintenance", value: "maintenance",  color: T.rose,    bg: T.roseLt    },
];

export default function Trucks() {
    const [trucks,        setTrucks]        = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);
    const [search,        setSearch]        = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [statusFilter,  setStatusFilter]  = useState("all");
    const [ownerFilter,   setOwnerFilter]   = useState("");   // owner name filter
    const [page,          setPage]          = useState(0);
    const [perPage,       setPerPage]       = useState(15);
    const [menuAnchor,    setMenuAnchor]     = useState(null);
    const [menuTruckId,   setMenuTruckId]   = useState(null);
    const navigate = useNavigate();

    const fetchTrucks = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await getData(API_ENDPOINTS.getTrucks, appliedSearch ? { search: appliedSearch } : {});
            if (res && Array.isArray(res.result)) {
                setTrucks(res.result.map(t => ({
                    ...t,
                    status: t.tagged_to_trip ? "On-Trip" : "Active",
                })));
            } else {
                setError("Unexpected data format from server."); setTrucks([]);
            }
        } catch (_) {
            setError("Failed to load trucks."); setTrucks([]);
        } finally {
            setLoading(false);
        }
    }, [appliedSearch]);

    useEffect(() => { fetchTrucks(); }, [fetchTrucks]);

    // Unique owners for filter dropdown
    const ownerNames = useMemo(() => {
        const names = [...new Set(trucks.map(t => t.truck_owner_name).filter(Boolean))].sort();
        return names;
    }, [trucks]);

    const filtered = useMemo(() => trucks.filter(t => {
        const matchStatus = statusFilter === "all" ||
            t.status.toLowerCase().replace(/\s+/g, "-") === statusFilter;
        const matchOwner  = !ownerFilter || t.truck_owner_name === ownerFilter;
        return matchStatus && matchOwner;
    }), [trucks, statusFilter, ownerFilter]);

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated  = filtered.slice(page * perPage, (page + 1) * perPage);

    const onTripCount    = trucks.filter(t => t.status === "On-Trip").length;
    const activeCount    = trucks.filter(t => t.status === "Active").length;
    const inactiveCount  = trucks.filter(t => t.status === "Inactive" || t.status === "Maintenance").length; // eslint-disable-line no-unused-vars

    const handleSearch   = e => { if (e.key === "Enter") { setAppliedSearch(search); setPage(0); } };
    const handleClear    = () => { setSearch(""); setAppliedSearch(""); setPage(0); };
    const handleStatus   = v => { setStatusFilter(v); setPage(0); };
    const handleOwner    = v => { setOwnerFilter(v);  setPage(0); };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Trucks
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Fleet management — all registered trucks
                    </Typography>
                </Box>

                {/* Summary pills */}
                {!loading && (
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        {[
                            { label: "Total",    value: trucks.length, bg: "rgba(255,255,255,0.15)", color: "#fff" },
                            { label: "On Trip",  value: onTripCount,   bg: T.indigoLt, color: T.indigo },
                            { label: "Active",   value: activeCount,   bg: T.emeraldLt,color: T.emerald },
                        ].map(s => (
                            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: s.bg, borderRadius: "10px", px: 1.5, py: 0.7, border: s.color === "#fff" ? "1px solid rgba(255,255,255,0.25)" : `1px solid ${s.color}22` }}>
                                <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: s.color, opacity: 0.85 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* ── Toolbar ── */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2.5 }}>
                {/* Search */}
                <TextField
                    placeholder="Search by name or truck number…"
                    size="small" value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                    sx={{ flex: "1 1 240px", maxWidth: 360 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.muted }} /></InputAdornment>,
                        endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={handleClear}><ClearIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                {/* Status filter pills */}
                <Box sx={{ display: "flex", gap: 0.75, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", p: 0.5, flexWrap: "wrap" }}>
                    {STATUS_FILTERS.map(f => (
                        <FilterPill key={f.value} label={f.label} active={statusFilter === f.value} color={f.color} bg={f.bg} onClick={() => handleStatus(f.value)} />
                    ))}
                </Box>

                {/* Owner filter */}
                {ownerNames.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", px: 1.5, py: 0.6 }}>
                        <ManageAccountsRoundedIcon sx={{ fontSize: 16, color: T.muted }} />
                        <Select
                            value={ownerFilter}
                            onChange={e => handleOwner(e.target.value)}
                            displayEmpty size="small"
                            variant="standard"
                            disableUnderline
                            sx={{ fontSize: 13, minWidth: 140, color: ownerFilter ? T.teal : T.muted, fontWeight: ownerFilter ? 700 : 400, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        >
                            <MenuItem value=""><em style={{ color: T.muted }}>All Owners</em></MenuItem>
                            {ownerNames.map(name => (
                                <MenuItem key={name} value={name} sx={{ fontSize: 13 }}>{name}</MenuItem>
                            ))}
                        </Select>
                        {ownerFilter && (
                            <IconButton size="small" onClick={() => handleOwner("")} sx={{ p: 0.3 }}>
                                <ClearIcon sx={{ fontSize: 14, color: T.muted }} />
                            </IconButton>
                        )}
                    </Box>
                )}

                {/* Result count */}
                {!loading && (
                    <Typography sx={{ fontSize: 13, color: T.muted, ml: "auto" }}>
                        {filtered.length} truck{filtered.length !== 1 ? "s" : ""}
                    </Typography>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

            {/* ── Table ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: T.bg }}>
                                {["Truck", "Number", "Owner", "Capacity", "Status", "Rating", ""].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({ length: perPage }).map((_, i) => <SkeletonRow key={i} />)
                                : paginated.length === 0
                                ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <LocalShippingRoundedIcon sx={{ fontSize: 40, color: T.border, mb: 1, display: "block", mx: "auto" }} />
                                            <Typography fontSize={13} color="text.secondary">No trucks found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                                : paginated.map(truck => (
                                    <TableRow key={truck.truck_id} hover
                                        sx={{ cursor: "pointer", "&:last-child td": { border: 0 }, "&:hover": { bgcolor: T.tealLt + "44" } }}
                                        onClick={() => navigate(`/app/truck-details/${encryptId(truck.truck_id)}`)}>

                                        {/* Truck name + pic */}
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: T.tealLt, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    {truck.truck_pic
                                                        ? <img src={truck.truck_pic} alt={truck.truck_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                        : <LocalShippingRoundedIcon sx={{ fontSize: 20, color: T.teal }} />
                                                    }
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{truck.truck_name}</Typography>
                                                    {truck.truck_model && (
                                                        <Typography sx={{ fontSize: 11, color: T.muted }}>{truck.truck_model}</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Reg number */}
                                        <TableCell>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: "monospace" }}>{truck.truck_no}</Typography>
                                        </TableCell>

                                        {/* Owner */}
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                                <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: T.orangeLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <PersonRoundedIcon sx={{ fontSize: 13, color: T.orange }} />
                                                </Box>
                                                <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500 }}>
                                                    {truck.truck_owner_name || "Unknown"}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Capacity with proper units */}
                                        <TableCell>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                                                {fmtCapacity(truck.capacity_inkgs, truck.capacity_inspace)}
                                            </Typography>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell><StatusChip status={truck.status} /></TableCell>

                                        {/* Rating */}
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Rating value={truck.average_rating || 0} precision={0.5} readOnly size="small"
                                                    sx={{ "& .MuiRating-iconFilled": { color: T.amber }, "& .MuiRating-iconEmpty": { color: T.border } }} />
                                                {truck.average_rating > 0 && (
                                                    <Typography sx={{ fontSize: 11, color: T.muted }}>{Number(truck.average_rating).toFixed(1)}</Typography>
                                                )}
                                            </Box>
                                        </TableCell>

                                        {/* View action */}
                                        <TableCell padding="none">
                                            <Tooltip title="View details">
                                                <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/app/truck-details/${encryptId(truck.truck_id)}`); }}
                                                    sx={{ color: T.muted }}>
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!loading && filtered.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: 13, color: T.muted }}>
                            Showing {Math.min(page * perPage + 1, filtered.length)}–{Math.min((page + 1) * perPage, filtered.length)} of {filtered.length}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }} size="small"
                                sx={{ borderRadius: "10px", fontSize: 13 }}>
                                {[15, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                            </Select>
                            <IconButton size="small" onClick={() => setPage(p => Math.max(p-1, 0))} disabled={page === 0}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                                {page + 1} / {totalPages}
                            </Typography>
                            <IconButton size="small" onClick={() => setPage(p => Math.min(p+1, totalPages-1))} disabled={page >= totalPages-1}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowRight sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Card>
        </Box>
    );
}
